/** Base64 chars decoded per atob() call. Multiple of 4 so each slice stays valid. */
const BASE64_CHUNK_SIZE = 0x8000;

/** How long the object URL stays alive after the click. */
export const OBJECT_URL_TTL_MS = 10_000;

/**
 * Decode a base64 string to bytes in chunks, keeping peak memory low.
 * The all-at-once decode throws on iOS Safari for large files due to its
 * tight per-tab memory ceiling.
 */
export function base64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  const bytes = new Uint8Array((base64.length / 4) * 3 - padding);

  let offset = 0;
  for (let i = 0; i < base64.length; i += BASE64_CHUNK_SIZE) {
    const binary = atob(base64.slice(i, i + BASE64_CHUNK_SIZE));
    for (let j = 0; j < binary.length; j++) {
      bytes[offset++] = binary.charCodeAt(j);
    }
  }
  return bytes;
}

/** Trigger a browser download of already-decoded content. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Defer revoke: iOS Safari fetches the blob asynchronously after click(),
  // so revoking inline aborts large downloads.
  setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_TTL_MS);
}

/** Trigger a browser download of a file served at a URL, e.g. a published installer. */
export function downloadUrl(url: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/** One form field; a name may repeat to send a list. */
export type FormField = readonly [name: string, value: string];

/**
 * The longest a download iframe stays. A saved attachment never fires its `load`, and removing
 * the iframe cancels a request the server is still preparing, so it outlasts any export.
 */
export const DOWNLOAD_FRAME_MAX_LIFETIME_MS = 30 * 60_000;

const DOWNLOAD_FRAME_PREFIX = 'download-frame-';

export interface DownloadFrameOptions {
  /** A response loaded instead of downloading: its body text, or null when it cannot be read. */
  readonly onErrorResponse: (body: string | null) => void;
}

/**
 * Body text of what loaded into `iframe`; null when it cannot be read (cross-origin),
 * undefined while it still holds its initial blank document.
 */
function loadedBody(iframe: HTMLIFrameElement): string | null | undefined {
  try {
    const doc = iframe.contentDocument;
    if (!doc) return null;
    if (doc.URL === 'about:blank') return undefined;
    return doc.body?.textContent ?? null;
  } catch {
    return null;
  }
}

/**
 * Attaches a hidden iframe that reports the first response loading into it, then removes itself;
 * otherwise it is removed after DOWNLOAD_FRAME_MAX_LIFETIME_MS. Returns it with its cleanup.
 */
function attachDownloadFrame({ onErrorResponse }: DownloadFrameOptions): {
  iframe: HTMLIFrameElement;
  cleanup: () => void;
} {
  const iframe = document.createElement('iframe');
  iframe.name = `${DOWNLOAD_FRAME_PREFIX}${crypto.randomUUID()}`;
  iframe.hidden = true;
  iframe.tabIndex = -1;
  iframe.setAttribute('aria-hidden', 'true');

  const onLoad = (): void => {
    const body = loadedBody(iframe);
    if (body === undefined) return;
    cleanup();
    onErrorResponse(body);
  };

  const timer = setTimeout(() => cleanup(), DOWNLOAD_FRAME_MAX_LIFETIME_MS);

  function cleanup(): void {
    clearTimeout(timer);
    iframe.removeEventListener('load', onLoad);
    iframe.remove();
  }

  // Appended before listening: the initial blank document may fire `load` on insertion.
  document.body.appendChild(iframe);
  iframe.addEventListener('load', onLoad);
  return { iframe, cleanup };
}

/**
 * Loads `url` into a hidden iframe, so the browser saves the attachment it answers and an error
 * answer reaches `onErrorResponse` instead of the page. Returns a cleanup that removes the iframe.
 */
export function openDownloadFrame(url: string, options: DownloadFrameOptions): () => void {
  const { iframe, cleanup } = attachDownloadFrame(options);
  iframe.src = url;
  return cleanup;
}

/** Posts a hidden form to `url` into a download frame, as `openDownloadFrame` loads a URL. */
export function submitDownloadForm(
  url: string,
  fields: readonly FormField[],
  options: DownloadFrameOptions
): () => void {
  const { iframe, cleanup } = attachDownloadFrame(options);

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;
  form.enctype = 'application/x-www-form-urlencoded';
  form.target = iframe.name;
  form.hidden = true;

  for (const [name, value] of fields) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  try {
    form.submit();
  } catch (err) {
    cleanup();
    throw err;
  } finally {
    form.remove();
  }
  return cleanup;
}

/** Trigger a browser download of base64-encoded file content. */
export function downloadBase64File(base64: string, filename: string, mimeType: string): void {
  downloadBlob(new Blob([base64ToUint8Array(base64)], { type: mimeType }), filename);
}

/**
 * Format file size for display
 * Handles edge cases: negative numbers, very large files (up to PB)
 */
export function formatFileSize(bytes: number): string {
  // Handle invalid input
  if (bytes < 0) return '0 B';
  if (bytes === 0) return '0 B';
  if (!isFinite(bytes)) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Clamped at both ends: a fraction of a byte would index before the array, a petabyte past it.
  const index = Math.min(Math.max(i, 0), sizes.length - 1);

  return `${Math.round((bytes / Math.pow(k, index)) * 10) / 10} ${sizes[index]}`;
}
